import { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  Tooltip,
  alpha
} from '@mui/material';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { skillsStore } from 'stores/skillsStore';
import { personalDataStore } from 'stores/personalDataStore';
import { useNavigate } from 'react-router-dom';
import PaymentCard from 'components/PaymentCard';
import { v4 as uuidv4 } from 'uuid';
import UploadAvatar from 'components/Profile/UploadAvatar';
import UserForm from 'components/Profile/UserForm';
import { panelSx, SectionHeader } from 'components/Profile/profileStyles';
import { PATHS } from 'routes/paths';

const getGradeColor = (grade: number) => {
  if (grade >= 9) return 'success';
  if (grade >= 7) return 'secondary';
  if (grade >= 4) return 'warning';
  return 'error';
};

const getGradeLabel = (grade: number) => {
  if (grade >= 9) return 'Эксперт';
  if (grade >= 7) return 'Продвинутый';
  if (grade >= 5) return 'Средний';
  return 'Начинающий';
};

const EmptyState = ({
  icon,
  title,
  description,
  action
}: {
  icon: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <Stack
    alignItems="center"
    spacing={1.5}
    sx={{
      textAlign: 'center',
      py: { xs: 4, sm: 6 },
      px: 2,
      borderRadius: (t) => `${t.shape.borderRadius}px`,
      backgroundColor: (t) => alpha(t.palette.grey[500], 0.04)
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        color: 'text.disabled',
        backgroundColor: (t) => alpha(t.palette.grey[500], 0.08)
      }}
    >
      <Icon icon={icon} width={28} height={28} />
    </Box>
    <Typography variant="h6" sx={{ color: 'text.primary' }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
      {description}
    </Typography>
    {action}
  </Stack>
);

export const SkillCard = ({
  skill,
  grade,
  selfValue
}: {
  skill: string;
  grade: number;
  selfValue?: number;
}) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      boxShadow: 'none',
      borderColor: 'grey.300',
      borderRadius: (t) => `${t.shape.borderRadiusSm}px`,
      transition: 'border-color 120ms, box-shadow 120ms',
      '&:hover': {
        borderColor: 'primary.light',
        boxShadow: (t) => t.customShadows.z1
      }
    }}
  >
    <CardContent sx={{ p: 3, pb: '20px !important' }}>
      <Stack spacing={2}>
        <Typography fontWeight={600} component="h3" noWrap sx={{ color: 'text.primary' }}>
          {skill}
        </Typography>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="body2" color="text.secondary">
              Уровень: {grade}/10
            </Typography>
            <Chip label={getGradeLabel(grade)} color={getGradeColor(grade) as any} size="small" />
          </Stack>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={(grade / 10) * 100}
              color={getGradeColor(grade) as any}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: (t) => alpha(t.palette.grey[500], 0.16)
              }}
            />
            {selfValue !== undefined && (
              <Tooltip title={'Ваша самооценка по этому навыку'}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -3,
                    left: `${selfValue * 10}%`,
                    width: 3,
                    height: 14,
                    backgroundColor: '#4F64FA',
                    borderRadius: 1,
                    transform: 'translateX(-50%)',
                    zIndex: 1
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const Profile = observer(() => {
  const skipPayment = process.env.REACT_APP_SKIP_PAYMENT === 'true';
  const isHrDemo = process.env.REACT_APP_IS_HR_DEMO === 'true';

  const navigate = useNavigate();

  const isForce = useMemo(
    () => location.search.includes('force=true') || !personalDataStore.user?.lastName,
    [location.search]
  );

  const handleProceedToPayment = () => {
    const uuid = uuidv4();
    navigate(`/payment/${uuid}?backTo=profile`);
  };

  const handleProceedToInterview = () => {
    if (!personalDataStore.requestedInterviews.length) {
      navigate(PATHS.videoInterview.checkDevices);
    } else {
      navigate(
        PATHS.requestedInterviews.vacancyInfo.replace(
          ':id',
          personalDataStore.requestedInterviews[0].id
        )
      );
    }
  };

  const allTestedSkills = Object.entries(personalDataStore.allTestedSkills).filter(
    ([title]) => skillsStore.skillsWithGrade[title]
  );

  return (
    <Stack spacing={6} sx={{ height: '100%', overflow: 'auto', mr: -3, pr: 3 }}>
      <UploadAvatar />
      <UserForm />
      {!isForce ? (
        <>
          <Paper elevation={0} sx={panelSx}>
            <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
              <SectionHeader
                title="Оценка ваших навыков"
                caption="Оценка на основе прошлых интервью с учётом ваших самооценок (отмечены синим цветом на шкале)."
              />
              {allTestedSkills.length ? (
                <Grid container spacing={3}>
                  {allTestedSkills.map(([title, grade]) => (
                    <Grid item xs={12} sm={6} md={4} key={title}>
                      <SkillCard
                        skill={title}
                        grade={grade}
                        selfValue={skillsStore.skillsWithGrade[title]}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : skillsStore.loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : (
                <EmptyState
                  icon="mdi:chart-box-outline"
                  title="Навыки ещё не оценены"
                  description="Пройдите интервью, чтобы получить оценку ваших навыков и подробный аналитический отчёт."
                  action={
                    !isHrDemo ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleProceedToInterview}
                        startIcon={<Icon icon="mdi:play-circle-outline" />}
                        sx={{ mt: 1 }}
                      >
                        Пройти интервью
                      </Button>
                    ) : undefined
                  }
                />
              )}
            </Box>
          </Paper>

          {!skipPayment ? (
            <Paper elevation={0} sx={panelSx}>
              <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
                <SectionHeader title="Платежи" />
                {personalDataStore.payments.length > 0 ? (
                  <Grid container spacing={3}>
                    {personalDataStore.payments.map((payment) => (
                      <Grid item xs={12} sm={6} md={4} key={payment.id}>
                        <PaymentCard payment={payment} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <EmptyState
                    icon="mdi:credit-card-outline"
                    title="Платежей пока нет"
                    description="Для прохождения интервью и получения подробного аналитического отчёта необходимо внести разовую оплату."
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleProceedToPayment}
                        startIcon={<Icon icon="mdi:credit-card-outline" />}
                        sx={{ mt: 1 }}
                      >
                        Перейти к оплате
                      </Button>
                    }
                  />
                )}
              </Box>
            </Paper>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
});

export default Profile;
